import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { calculateRiskReward, validateTradeSetup, calculateTradeScore, checkTradingPlanViolations } from '@/utils/calculations';
import { getTradingPlan, getTradeJournal, saveValidatedTrade } from '@/utils/localStorage';
import { toast } from 'sonner';
import { Upload, AlertTriangle, CheckCircle2, TrendingUp, Shield, Copy, Check, Radio } from 'lucide-react';
import { useLocation } from 'react-router-dom';

export default function TradeSetupValidator() {
  const location = useLocation();
  const signalData = location.state?.signalData;
  
  const [formData, setFormData] = useState({
    entryPrice: '',
    stopLoss: '',
    takeProfit: '',
    setupType: '',
    signalId: signalData?.signalId || null
  });
  
  const [checklist, setChecklist] = useState({
    // Market Structure
    clearTrendExists: false,
    notInSidewaysRange: false,
    // Pullback Quality
    pullbackMultipleCandles: false,
    noLargeReversalCandle: false,
    // Entry Trigger
    entryOnBreakOrClose: false,
    // Risk Placement
    stopBeyondStructure: false,
    takeProfitMin2R: false
  });
  
  const [chartScreenshot, setChartScreenshot] = useState(null);
  const [chartPreview, setChartPreview] = useState(null);
  const [validation, setValidation] = useState(null);
  const [rrData, setRrData] = useState(null);
  const [planViolations, setPlanViolations] = useState(null);
  const [tradingPlan, setTradingPlan] = useState(null);
  const [preTradeId, setPreTradeId] = useState(null);
  const [copied, setCopied] = useState(false);
  
  useEffect(() => {
    const plan = getTradingPlan();
    setTradingPlan(plan);
    
    // Pre-fill from signal data if available
    if (signalData) {
      setFormData(prev => ({
        ...prev,
        entryPrice: signalData.price.toString(),
        setupType: signalData.signalType,
        signalId: signalData.signalId
      }));
      
      toast.success(`Signal data loaded: ${signalData.symbol} at ${signalData.price}`, {
        duration: 3000
      });
    }
  }, [signalData]);
  
  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Auto-calculate R:R when all fields are filled
    const updated = { ...formData, [field]: value };
    if (updated.entryPrice && updated.stopLoss && updated.takeProfit) {
      const rr = calculateRiskReward(updated.entryPrice, updated.stopLoss, updated.takeProfit);
      setRrData(rr);
      if (rr && parseFloat(rr.riskRewardRatio) >= 2.0) {
        setChecklist(prev => ({ ...prev, takeProfitMin2R: true }));
      } else {
        setChecklist(prev => ({ ...prev, takeProfitMin2R: false }));
      }
    }
  };
  
  const handleChecklistChange = (key) => {
    setChecklist(prev => ({ ...prev, [key]: !prev[key] }));
  };
  
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size should be less than 5MB');
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setChartScreenshot(reader.result);
        setChartPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };
  
  const handleValidate = () => {
    if (!formData.entryPrice || !formData.stopLoss || !formData.takeProfit || !formData.setupType) {
      toast.error('Please fill all required fields');
      return;
    }
    
    const validationResult = validateTradeSetup(formData, checklist, rrData);
    setValidation(validationResult);
    
    // Check trading plan violations
    const trades = getTradeJournal();
    const violations = checkTradingPlanViolations(formData, checklist, rrData, tradingPlan, trades);
    setPlanViolations(violations);
    
    // Generate Pre-Trade ID if validation passes
    if (validationResult.isValid && !violations.hasViolations) {
      try {
        const id = saveValidatedTrade({
          ...formData,
          rrData,
          checklist,
          chartScreenshot
        });
        setPreTradeId(id);
        toast.success('Trade validated! Pre-Trade ID generated.');
      } catch (error) {
        toast.error('Failed to generate Pre-Trade ID');
      }
    } else if (violations.hasViolations) {
      setPreTradeId(null);
      toast.error('This trade breaks your trading plan');
    } else {
      setPreTradeId(null);
      toast.error('Trade does not meet plan');
    }
  };
  
  const handleCopyId = () => {
    if (preTradeId) {
      navigator.clipboard.writeText(preTradeId);
      setCopied(true);
      toast.success('Pre-Trade ID copied!');
      setTimeout(() => setCopied(false), 2000);
    }
  };
  
  const handleReset = () => {
    setFormData({
      entryPrice: '',
      stopLoss: '',
      takeProfit: '',
      setupType: ''
    });
    setChecklist({
      clearTrendExists: false,
      notInSidewaysRange: false,
      pullbackMultipleCandles: false,
      noLargeReversalCandle: false,
      entryOnBreakOrClose: false,
      stopBeyondStructure: false,
      takeProfitMin2R: false
    });
    setChartScreenshot(null);
    setChartPreview(null);
    setValidation(null);
    setRrData(null);
    setPlanViolations(null);
    setPreTradeId(null);
    setCopied(false);
  };
  
  const getValidationData = () => {
    return {
      ...formData,
      checklist,
      chartScreenshot,
      riskRewardRatio: rrData?.riskRewardRatio,
      riskPercent: rrData?.riskPercent,
      rewardPercent: rrData?.rewardPercent,
      isValid: validation?.isValid
    };
  };
  
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-4xl md:text-5xl font-black tracking-tighter text-foreground mb-2">
          Trade Setup Validator
        </h1>
        <p className="text-muted-foreground font-medium">
          Validate your trade setup before execution. Ensure discipline and proper risk management.
        </p>
      </div>
      
      {signalData && (
        <Card className="p-4 border-primary/40 bg-primary/5 mb-6">
          <div className="flex items-center space-x-3">
            <Radio className="w-5 h-5 text-primary" />
            <div>
              <p className="text-sm font-semibold text-foreground">
                Trade setup pre-filled from signal
              </p>
              <p className="text-xs text-muted-foreground">
                {signalData.symbol} - {signalData.signalType.toUpperCase()} at {signalData.price} ({signalData.timeframe})
              </p>
            </div>
          </div>
        </Card>
      )}
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
        <div className="space-y-6">
          <Card className="p-6 border-border/40 bg-card">
            <h2 className="text-xl font-bold text-foreground mb-4">Trade Details</h2>
            
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="entryPrice" className="text-foreground font-semibold mb-2 block">
                    Entry Price *
                  </Label>
                  <Input
                    id="entryPrice"
                    data-testid="entry-price-input"
                    type="number"
                    step="any"
                    value={formData.entryPrice}
                    onChange={(e) => handleInputChange('entryPrice', e.target.value)}
                    placeholder="0.00"
                    className="h-11 bg-secondary/20 border-border/50 font-mono"
                  />
                </div>
                
                <div>
                  <Label htmlFor="stopLoss" className="text-foreground font-semibold mb-2 block">
                    Stop Loss *
                  </Label>
                  <Input
                    id="stopLoss"
                    data-testid="stop-loss-input"
                    type="number"
                    step="any"
                    value={formData.stopLoss}
                    onChange={(e) => handleInputChange('stopLoss', e.target.value)}
                    placeholder="0.00"
                    className="h-11 bg-secondary/20 border-border/50 font-mono"
                  />
                </div>
                
                <div>
                  <Label htmlFor="takeProfit" className="text-foreground font-semibold mb-2 block">
                    Take Profit *
                  </Label>
                  <Input
                    id="takeProfit"
                    data-testid="take-profit-input"
                    type="number"
                    step="any"
                    value={formData.takeProfit}
                    onChange={(e) => handleInputChange('takeProfit', e.target.value)}
                    placeholder="0.00"
                    className="h-11 bg-secondary/20 border-border/50 font-mono"
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="setupType" className="text-foreground font-semibold mb-2 block">
                  Setup Type *
                </Label>
                <Select value={formData.setupType} onValueChange={(value) => handleInputChange('setupType', value)}>
                  <SelectTrigger data-testid="setup-type-select" className="h-11 bg-secondary/20 border-border/50">
                    <SelectValue placeholder="Select setup type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="breakout">Breakout</SelectItem>
                    <SelectItem value="pullback">Pullback</SelectItem>
                    <SelectItem value="reversal">Reversal</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="chartUpload" className="text-foreground font-semibold mb-2 block">
                  Chart Screenshot
                </Label>
                <div className="border-2 border-dashed border-border/50 rounded-md p-6 text-center hover:border-primary/50 transition-colors cursor-pointer">
                  <input
                    type="file"
                    id="chartUpload"
                    data-testid="chart-upload-input"
                    accept="image/*"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                  <label htmlFor="chartUpload" className="cursor-pointer">
                    <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground font-medium">
                      {chartPreview ? 'Image uploaded - Click to change' : 'Click to upload chart screenshot'}
                    </p>
                  </label>
                </div>
                {chartPreview && (
                  <div className="mt-4 rounded-md overflow-hidden border border-border/40">
                    <img src={chartPreview} alt="Chart preview" className="w-full h-auto" />
                  </div>
                )}
              </div>
            </div>
          </Card>
          
          <Card className="p-6 border-border/40 bg-card">
            <h2 className="text-xl font-bold text-foreground mb-4">Trading Model Checklist</h2>
            <p className="text-sm text-muted-foreground mb-4">Confirm each criterion. Unchecked items will heavily reduce your validation score.</p>
            
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-bold text-primary mb-2">Market Structure</h3>
                <div className="space-y-2">
                  <div className="flex items-center justify-between py-3 px-4 rounded-md bg-secondary/30 border border-border/30">
                    <Label htmlFor="clearTrendExists" className="text-foreground font-medium cursor-pointer flex-1">
                      Clear trend exists (higher highs or lower lows)
                    </Label>
                    <Switch
                      id="clearTrendExists"
                      data-testid="checklist-clearTrendExists"
                      checked={checklist.clearTrendExists}
                      onCheckedChange={() => handleChecklistChange('clearTrendExists')}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between py-3 px-4 rounded-md bg-secondary/30 border border-border/30">
                    <Label htmlFor="notInSidewaysRange" className="text-foreground font-medium cursor-pointer flex-1">
                      Price not inside sideways range
                    </Label>
                    <Switch
                      id="notInSidewaysRange"
                      data-testid="checklist-notInSidewaysRange"
                      checked={checklist.notInSidewaysRange}
                      onCheckedChange={() => handleChecklistChange('notInSidewaysRange')}
                    />
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className="text-sm font-bold text-primary mb-2">Pullback Quality</h3>
                <div className="space-y-2">
                  <div className="flex items-center justify-between py-3 px-4 rounded-md bg-secondary/30 border border-border/30">
                    <Label htmlFor="pullbackMultipleCandles" className="text-foreground font-medium cursor-pointer flex-1">
                      Pullback has multiple small candles or consolidation
                    </Label>
                    <Switch
                      id="pullbackMultipleCandles"
                      data-testid="checklist-pullbackMultipleCandles"
                      checked={checklist.pullbackMultipleCandles}
                      onCheckedChange={() => handleChecklistChange('pullbackMultipleCandles')}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between py-3 px-4 rounded-md bg-secondary/30 border border-border/30">
                    <Label htmlFor="noLargeReversalCandle" className="text-foreground font-medium cursor-pointer flex-1">
                      No single large reversal candle
                    </Label>
                    <Switch
                      id="noLargeReversalCandle"
                      data-testid="checklist-noLargeReversalCandle"
                      checked={checklist.noLargeReversalCandle}
                      onCheckedChange={() => handleChecklistChange('noLargeReversalCandle')}
                    />
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className="text-sm font-bold text-primary mb-2">Entry Trigger</h3>
                <div className="space-y-2">
                  <div className="flex items-center justify-between py-3 px-4 rounded-md bg-secondary/30 border border-border/30">
                    <Label htmlFor="entryOnBreakOrClose" className="text-foreground font-medium cursor-pointer flex-1">
                      Entry occurs on break of pullback or continuation candle close
                    </Label>
                    <Switch
                      id="entryOnBreakOrClose"
                      data-testid="checklist-entryOnBreakOrClose"
                      checked={checklist.entryOnBreakOrClose}
                      onCheckedChange={() => handleChecklistChange('entryOnBreakOrClose')}
                    />
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className="text-sm font-bold text-primary mb-2">Risk Placement</h3>
                <div className="space-y-2">
                  <div className="flex items-center justify-between py-3 px-4 rounded-md bg-secondary/30 border border-border/30">
                    <Label htmlFor="stopBeyondStructure" className="text-foreground font-medium cursor-pointer flex-1">
                      Stop placed beyond pullback structure
                    </Label>
                    <Switch
                      id="stopBeyondStructure"
                      data-testid="checklist-stopBeyondStructure"
                      checked={checklist.stopBeyondStructure}
                      onCheckedChange={() => handleChecklistChange('stopBeyondStructure')}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between py-3 px-4 rounded-md bg-secondary/30 border border-border/30">
                    <Label htmlFor="takeProfitMin2R" className="text-foreground font-medium cursor-pointer flex-1">
                      Take profit allows minimum 2R
                    </Label>
                    <Switch
                      id="takeProfitMin2R"
                      data-testid="checklist-takeProfitMin2R"
                      checked={checklist.takeProfitMin2R}
                      onCheckedChange={() => handleChecklistChange('takeProfitMin2R')}
                      disabled={true}
                    />
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex space-x-3 mt-6">
              <Button
                onClick={handleValidate}
                data-testid="validate-trade-btn"
                className="flex-1 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold h-11"
              >
                <TrendingUp className="w-4 h-4 mr-2" />
                Validate Trade
              </Button>
              <Button
                onClick={handleReset}
                data-testid="reset-validator-btn"
                variant="outline"
                className="h-11 border-border hover:bg-accent"
              >
                Reset
              </Button>
            </div>
          </Card>
        </div>
        
        <div className="space-y-6">
          {rrData && (
            <Card className="p-6 border-border/40 bg-card">
              <h2 className="text-xl font-bold text-foreground mb-4">Risk/Reward Analysis</h2>
              
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-4 rounded-md bg-destructive/10 border border-destructive/30">
                  <p className="text-sm text-muted-foreground mb-1">Risk</p>
                  <p className="text-2xl font-bold font-mono text-destructive" data-testid="risk-percent">
                    {rrData.riskPercent}%
                  </p>
                </div>
                
                <div className="text-center p-4 rounded-md bg-success/10 border border-success/30">
                  <p className="text-sm text-muted-foreground mb-1">Reward</p>
                  <p className="text-2xl font-bold font-mono text-success" data-testid="reward-percent">
                    {rrData.rewardPercent}%
                  </p>
                </div>
                
                <div className="text-center p-4 rounded-md bg-primary/10 border border-primary/30">
                  <p className="text-sm text-muted-foreground mb-1">R:R Ratio</p>
                  <p className="text-2xl font-bold font-mono text-primary" data-testid="rr-ratio">
                    {rrData.riskRewardRatio}
                  </p>
                </div>
              </div>
            </Card>
          )}
          
          {preTradeId && (
            <Card className="p-6 border-2 border-success bg-success/10" data-testid="pretrade-id-card">
              <div className="flex items-start space-x-4">
                <CheckCircle2 className="w-8 h-8 text-success flex-shrink-0 mt-1" />
                
                <div className="flex-1">
                  <h3 className="text-xl font-bold mb-2 text-success">
                    ✅ Pre-Trade Validation Complete
                  </h3>
                  
                  <p className="text-foreground font-medium mb-3">
                    Your trade has been validated and a Pre-Trade ID has been generated. Use this ID when logging the trade in your journal.
                  </p>
                  
                  <div className="bg-background/50 p-4 rounded-md border border-success/30">
                    <Label className="text-sm font-semibold text-muted-foreground mb-2 block">Pre-Trade ID</Label>
                    <div className="flex items-center space-x-2">
                      <code className="flex-1 text-lg font-mono font-bold text-success bg-background px-3 py-2 rounded" data-testid="pretrade-id">
                        {preTradeId}
                      </code>
                      <Button
                        onClick={handleCopyId}
                        data-testid="copy-id-btn"
                        size="sm"
                        className="bg-success hover:bg-success/90 text-success-foreground"
                      >
                        {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2 italic">
                      ⏰ Valid for 24 hours. Copy this ID before executing your trade.
                    </p>
                  </div>
                </div>
              </div>
            </Card>
          )}
          
          {planViolations && planViolations.hasViolations && (
            <Card className="p-6 border-2 border-destructive bg-destructive/10" data-testid="plan-violation-card">
              <div className="flex items-start space-x-4">
                <Shield className="w-8 h-8 text-destructive flex-shrink-0 mt-1" />
                
                <div className="flex-1">
                  <h3 className="text-xl font-bold mb-2 text-destructive">
                    ⛔ This trade breaks your trading plan
                  </h3>
                  
                  <p className="text-foreground font-semibold mb-3">
                    You have violated the following rules:
                  </p>
                  
                  <ul className="space-y-2 mb-4">
                    {planViolations.violations.map((violation, idx) => (
                      <li key={idx} className="text-sm text-foreground flex items-start bg-background/50 p-2 rounded">
                        <span className="text-destructive mr-2 font-bold">✗</span>
                        {violation}
                      </li>
                    ))}
                  </ul>
                  
                  <p className="text-xs text-muted-foreground italic">
                    You can still log this trade in your journal, but it will be marked as a "Rule Violation Trade"
                  </p>
                </div>
              </div>
            </Card>
          )}
          
          {validation && (
            <Card className={`p-6 border-2 ${
              validation.isValid 
                ? 'border-success/50 bg-success/10' 
                : 'border-destructive/50 bg-destructive/10'
            }`}>
              <div className="flex items-start space-x-4">
                {validation.isValid ? (
                  <CheckCircle2 className="w-8 h-8 text-success flex-shrink-0 mt-1" />
                ) : (
                  <AlertTriangle className="w-8 h-8 text-destructive flex-shrink-0 mt-1" />
                )}
                
                <div className="flex-1">
                  <h3 className={`text-xl font-bold mb-2 ${
                    validation.isValid ? 'text-success' : 'text-destructive'
                  }`}>
                    {validation.isValid ? 'Valid Trade Setup' : validation.isMechanicalViolation ? 'This setup does not match your trading model' : 'Invalid Trade Setup'}
                  </h3>
                  
                  {validation.isValid ? (
                    <p className="text-foreground font-medium">
                      All mechanical criteria met. You may proceed with this trade according to your model.
                    </p>
                  ) : (
                    <div className="space-y-2">
                      <p className="text-foreground font-semibold">
                        {validation.isMechanicalViolation 
                          ? 'Your trading model requires all checklist items to be confirmed. The following criteria are not met:'
                          : 'Trade does not meet requirements. Consider skipping.'}
                      </p>
                      <div className="mt-3 space-y-2">
                        <p className="text-sm font-semibold text-muted-foreground">Violations detected:</p>
                        <ul className="space-y-1">
                          {validation.issues.map((issue, idx) => (
                            <li key={idx} className="text-sm text-foreground flex items-start bg-background/50 p-2 rounded">
                              <span className="text-destructive mr-2 font-bold">✗</span>
                              {issue}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </Card>
          )}
          
          {!rrData && !validation && !planViolations && (
            <Card className="p-8 border-border/40 bg-card h-full flex items-center justify-center">
              <div className="text-center text-muted-foreground">
                <TrendingUp className="w-16 h-16 mx-auto mb-4 opacity-20" />
                <p className="font-medium">Enter trade details to see risk/reward analysis</p>
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}